import React, { useEffect, useRef, useState, useCallback } from "react";
import {
	Box,
	Typography,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
} from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import relativeTime from "dayjs/plugin/relativeTime";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(utc);
dayjs.extend(relativeTime);
dayjs.extend(customParseFormat);

const BACKEND_WS_URL = process.env.REACT_APP_BACKEND_WS_URL;

// robust timestamp parser -> local time
function toLocal(ts) {
	if (!ts) return null;

	// ISO or anything dayjs can auto-parse with TZ
	const iso = dayjs(ts);
	if (iso.isValid() && /(Z|z|[+-]\d{2}:?\d{2})/.test(ts)) {
		return iso.local();
	}

	// DD/MM/YY HH:mm:ss (sample)
	const dmy = dayjs.utc(ts, "DD/MM/YY HH:mm:ss", true);
	if (dmy.isValid()) return dmy.local();

	// D/M/YYYY H:mm:ss
	const dmyLong = dayjs.utc(ts, "D/M/YYYY H:mm:ss", true);
	if (dmyLong.isValid()) return dmyLong.local();

	// Fallback: treat as UTC then local
	const asUtc = dayjs.utc(ts);
	return asUtc.isValid() ? asUtc.local() : null;
}

export default function MQTT({ deviceId, chassisNum }) {
	const wsRef = useRef(null);
	const [rows, setRows] = useState([]); // newest first

	const addRow = useCallback((msg) => {
		// msg is either JSON string or already parsed object
		let data = msg;
		if (typeof msg === "string") {
			try {
				data = JSON.parse(msg);
			} catch {
				// accept raw string without JSON wrapper
				data = { timestamp: dayjs().toISOString(), raw: msg };
			}
		}
		const local = toLocal(data.timestamp) || dayjs();
		const row = {
			tsLocal: local,
			tsStr: local.format("HH:mm:ss"),
			tsFromNow: local.fromNow(),
			raw: data.raw ?? "",
		};
		setRows((prev) => [row, ...prev].slice(0, 50));
	}, []);

	useEffect(() => {
		if (!deviceId) return;

		const ws = new WebSocket(
			`${BACKEND_WS_URL}ws/live/devices/${deviceId}/raw`
		);
		wsRef.current = ws;

		ws.onmessage = (evt) => {
			addRow(evt.data);
		};
		ws.onerror = (err) => console.error("WS error:", err);

		return () => {
			ws.close();
			wsRef.current = null;
		};
	}, [deviceId, addRow]);

	return (
		<Box sx={{ p: { xs: 1, sm: 2 } }}>
			<Stack
				direction={{ xs: "column", md: "row" }}
				alignItems={{ md: "center" }}
				justifyContent="space-between"
				spacing={1}
				mb={1}
			>
				<Typography variant="subtitle1">
					Live MQTT{" - "}
					<Typography variant="caption">{chassisNum}</Typography>
				</Typography>
				<Typography variant="body2" color="text.secondary">
					Showing latest {rows.length} of 50
				</Typography>
			</Stack>

			<TableContainer component={Paper} variant="outlined">
				<Table size="small" stickyHeader>
					<TableHead>
						<TableRow>
							<TableCell>Time (IST)</TableCell>
							<TableCell>Raw Payload</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{rows.map((r, idx) => (
							<TableRow key={idx} hover>
								<TableCell sx={{ whiteSpace: "nowrap" }}>
									{r.tsStr}
								</TableCell>
								<TableCell>
									<Box
										component="pre"
										sx={{
											m: 0,
											fontFamily:
												"ui-monospace, SFMono-Regular, Menlo, monospace",
											fontSize: 12,
											whiteSpace: "pre-wrap",
											wordBreak: "break-word",
										}}
									>
										{r.raw}
									</Box>
								</TableCell>
							</TableRow>
						))}
						{rows.length === 0 && (
							<TableRow>
								<TableCell
									colSpan={3}
									align="center"
									sx={{ py: 4, color: "text.secondary" }}
								>
									Waiting for messages…
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</TableContainer>
		</Box>
	);
}
